/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { useCallback, useState } from "react";
import PlannerMap from "./PlannerMap";
import { StickyContainer } from "react-sticky";
import { useResize } from "../../hooks";
import { usePlannerToasts } from "./hooks";
import ItineraryContainer from "../itinerary/ItineraryContainer";
import classNames from "classnames";
import { Helmet } from "react-helmet";
import { useTranslate } from "../../translate";

StickyContainer.displayName = "StickyContainer";

export const useWindowHeight = () => {
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    useResize(() => {
        setWindowHeight(window.innerHeight);
    });
    return windowHeight;
};

function Planner(props) {
    const translate = useTranslate();
    const {
        isMapOverlayVisible,
        mapOverlayText,
        itineraryId,
        itinerary,
        onFetchItinerary,
        viewport,
        setViewport,
        isSecondWayVisible,
        setIsSecondWayVisible,
        activeStopId,
        setActiveStopId,
        isMetadataInScrollView,
        setIsMetadataInScrollView,
        activePlaceStartId,
        setActivePlaceStartId,
        stopIdsByPlaceStartId,
        isAutoFollowEnabled,
        setIsAutoFollowEnabled,
        loopDirection,
        setLoopDirection,
        onMarkerClick,
        dayTopOffset,
        appHeaderHeight,
        enableChangeItinerary,
        onItineraryIdChange,
        onStartingPointChange,
        startingPoint,
        routeItineraries,
        inLauncherModal,
        stopsById,
        useTwoColumnLayout,
        sizeByStickyContent,
        setSizeByStickyContent,
        cardsOffsetTop,
        inPdfMode,
        activeSuggestionId,
        setActiveSuggestionId,
    } = props;
    const routeName = itinerary?.routeData?.routeName ?? "";
    const itineraryName = translate(itinerary?.itineraryData?.metadata, "name") || "";
    const [showToast, Toasts, toastsProps, toastsContainerRef] = usePlannerToasts();
    const mapTemplate = (
        <PlannerMap
            stopsById={stopsById}
            isMapOverlayVisible={isMapOverlayVisible}
            mapOverlayText={mapOverlayText}
            className="trpr__primary-layout-map"
            viewport={viewport}
            setViewport={setViewport}
            itinerary={itinerary}
            activeStopId={activeStopId}
            stopIdsByPlaceStartId={stopIdsByPlaceStartId}
            isAutoFollowEnabled={isAutoFollowEnabled}
            setIsAutoFollowEnabled={setIsAutoFollowEnabled}
            loopDirection={loopDirection}
            isSecondWayVisible={isSecondWayVisible}
            onMarkerClick={onMarkerClick}
            appHeaderHeight={appHeaderHeight}
            startingPoint={startingPoint}
            useTwoColumnLayout={useTwoColumnLayout}
            sizeByStickyContent={sizeByStickyContent}
            setSizeByStickyContent={setSizeByStickyContent}
            cardsOffsetTop={cardsOffsetTop}
            activeSuggestionId={activeSuggestionId}
        />
    );
    const renderMapInItinerary = useCallback(() => (useTwoColumnLayout ? null : mapTemplate), [
        useTwoColumnLayout,
        mapTemplate,
    ]);

    return (
        <StickyContainer
            className={classNames("trpr__primary-layout in-itinerary-route is-new-layout js-primary-layout", {
                "in-pdf-mode": inPdfMode,
            })}
        >
            {useTwoColumnLayout && <div className="trpr__primary-layout-shadow" aria-hidden />}
            {useTwoColumnLayout && mapTemplate}
            <ItineraryContainer
                activePlaceStartId={activePlaceStartId}
                activeStopId={activeStopId}
                dayTopOffset={dayTopOffset}
                enableChangeItinerary={enableChangeItinerary}
                inPlanner={true}
                isMetadataInScrollView={isMetadataInScrollView}
                isSecondWayVisible={isSecondWayVisible}
                itineraryId={itineraryId}
                itineraryItemsClassName="trpr__primary-layout-itinerary-items"
                itineraryMetadataClassName="trpr__primary-layout-itinerary-metadata"
                loaderClassName="trpr__primary-layout-content"
                loopDirection={loopDirection}
                onActivePlaceStartIdChange={setActivePlaceStartId}
                onActiveStopIdChange={setActiveStopId}
                onFetchItinerary={onFetchItinerary}
                onIsMetadataInScrollViewChange={setIsMetadataInScrollView}
                onIsSecondWayVisibleChange={setIsSecondWayVisible}
                onItineraryIdChange={onItineraryIdChange}
                onStartingPointChange={onStartingPointChange}
                renderMap={renderMapInItinerary}
                routeItineraries={routeItineraries}
                setLoopDirection={setLoopDirection}
                inLauncherModal={inLauncherModal}
                useTwoColumnLayout={useTwoColumnLayout}
                useOldLayout={false}
                sizeByStickyContent={sizeByStickyContent}
                showToast={showToast}
                activeSuggestionId={activeSuggestionId}
                setActiveSuggestionId={setActiveSuggestionId}
            />
            <div className="trpr__primary-layout-popups js-primary-layout-popups"></div>
            <div ref={toastsContainerRef} className="trpr__primary-layout-toasts js-primary-layout-toasts">
                <Toasts {...toastsProps}></Toasts>
            </div>
            <Helmet>
                <title>{itineraryName || routeName || "Loading..."} | Ontario Trip Explorer</title>
            </Helmet>
        </StickyContainer>
    );
}

export default Planner;
